// src/components/seo.js
import React, { useEffect } from "react";
import { Helmet } from "react-helmet";

const SITE_NAME = "TarotFate";
const SITE_URL = "https://tarotfate.com";
const DEFAULT_IMAGE = "https://tarotfate.com/icons/og-cover.jpg";
const DEFAULT_ROBOTS =
  "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

function absoluteUrl(value) {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("/")) return `${SITE_URL}${value}`;
  return `${SITE_URL}/${value}`;
}

function normalizeSchema(schema) {
  if (!schema) return null;
  if (Array.isArray(schema)) {
    return {
      "@context": "https://schema.org",
      "@graph": schema,
    };
  }
  if (schema["@context"]) return schema;
  return {
    "@context": "https://schema.org",
    ...schema,
  };
}

function normalizePathname(pathname = "/") {
  const normalized = String(pathname || "/").replace(/\/+$/, "");
  return normalized || "/";
}

function localizedPathPair(canonicalUrl) {
  try {
    const url = new URL(canonicalUrl || SITE_URL);
    const pathname = normalizePathname(url.pathname);
    const englishPath =
      pathname === "/es" ? "/" : pathname.startsWith("/es/") ? normalizePathname(pathname.slice(3)) : pathname;
    const spanishPath = englishPath === "/" ? "/es" : `/es${englishPath}`;

    return {
      english: `${SITE_URL}${englishPath}`,
      spanish: `${SITE_URL}${spanishPath}`,
      xDefault: `${SITE_URL}${englishPath}`,
    };
  } catch {
    return {
      english: `${SITE_URL}/`,
      spanish: `${SITE_URL}/es`,
      xDefault: `${SITE_URL}/`,
    };
  }
}

function defaultAlternates(canonicalUrl) {
  const localized = localizedPathPair(canonicalUrl);
  return [
    { lang: "x-default", href: localized.xDefault },
    { lang: "en", href: localized.english },
    { lang: "es", href: localized.spanish },
  ];
}

function defaultSchema({ title, description, canonicalUrl, imageUrl, lang }) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: SITE_NAME,
        url: SITE_URL,
        logo: {
          "@type": "ImageObject",
          url: `${SITE_URL}/icons/logo.webp`,
          width: 512,
          height: 512,
        },
      },
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        name: SITE_NAME,
        url: SITE_URL,
        inLanguage: lang,
        publisher: { "@id": `${SITE_URL}/#organization` },
        potentialAction: {
          "@type": "SearchAction",
          target: `${SITE_URL}/search?query={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@type": "WebPage",
        "@id": `${canonicalUrl}#webpage`,
        url: canonicalUrl,
        name: title,
        description,
        inLanguage: lang,
        isPartOf: { "@id": `${SITE_URL}/#website` },
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: imageUrl,
          width: 1200,
          height: 630,
        },
      },
    ],
  };
}

function upsertMeta(attribute, value, content, extraAttributes = {}) {
  if (!content) return;
  let tag = document.head.querySelector(`meta[${attribute}="${value}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, value);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
  tag.setAttribute("data-seo-managed", "true");
  Object.entries(extraAttributes).forEach(([key, attrValue]) => {
    if (attrValue) tag.setAttribute(key, attrValue);
  });
}

function upsertLink(rel, href, extraAttributes = {}) {
  if (!href) return;
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
  tag.setAttribute("data-seo-managed", "true");
  Object.entries(extraAttributes).forEach(([key, attrValue]) => {
    if (attrValue) tag.setAttribute(key, attrValue);
  });
}

export default function Seo({
  title,
  description,
  url,
  path,
  image = DEFAULT_IMAGE,
  schema,
  lang = "en",
  alternates = [],
  robots = DEFAULT_ROBOTS,
  type = "website",
  ogLocale = lang === "es" ? "es_ES" : "en_US",
  keywords, // Accepted for compatibility. Meta keywords are intentionally not rendered.
}) {
  void keywords;
  const canonicalUrl = absoluteUrl(url || path);
  const imageUrl = absoluteUrl(image || DEFAULT_IMAGE) || DEFAULT_IMAGE;
  const seoAlternates = alternates.length ? alternates : defaultAlternates(canonicalUrl);
  const structuredData = normalizeSchema(
    schema || defaultSchema({ title, description, canonicalUrl, imageUrl, lang })
  );

  useEffect(() => {
    if (typeof document === "undefined") return;

    document.documentElement.setAttribute("lang", lang);
    document.documentElement.setAttribute("translate", "no");
    document.documentElement.classList.add("notranslate");
    if (title) document.title = title;

    upsertMeta("name", "description", description);
    upsertMeta("name", "google", "notranslate");
    upsertMeta("name", "robots", robots);
    upsertMeta("name", "author", SITE_NAME);
    upsertMeta("name", "theme-color", "#014C75");

    upsertLink("canonical", canonicalUrl);

    document.head
      .querySelectorAll('link[rel="alternate"][data-seo-managed="true"]')
      .forEach((tag) => tag.remove());
    seoAlternates.forEach((alt) => {
      const tag = document.createElement("link");
      tag.setAttribute("rel", "alternate");
      tag.setAttribute("hrefLang", alt.lang);
      tag.setAttribute("href", alt.href);
      tag.setAttribute("data-seo-managed", "true");
      document.head.appendChild(tag);
    });

    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:locale", ogLocale);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", imageUrl);
    upsertMeta("property", "og:image:alt", title);
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", imageUrl);
    upsertMeta("name", "twitter:image:alt", title);
  }, [
    title,
    description,
    canonicalUrl,
    imageUrl,
    lang,
    robots,
    type,
    ogLocale,
    seoAlternates,
  ]);

  return (
    <>
      <Helmet>
      <html lang={lang} translate="no" className="notranslate" />
      <title>{title}</title>

      {description && <meta name="description" content={description} />}
      <meta name="google" content="notranslate" />
      <meta name="robots" content={robots} />
      <meta name="author" content={SITE_NAME} />
      <meta name="theme-color" content="#014C75" />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {seoAlternates.map((alt) => (
        <link
          key={`${alt.lang}-${alt.href}`}
          rel="alternate"
          hrefLang={alt.lang}
          href={alt.href}
        />
      ))}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:title" content={title} />
      {description && <meta property="og:description" content={description} />}
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={title} />

      </Helmet>
      {structuredData && (
        <script
          type="application/ld+json"
          data-seo-jsonld="true"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />
      )}
    </>
  );
}
